import { useEffect, useState } from "react";
import { Outlet, Navigate } from "react-router-dom";
import { getCurrentUser } from "../../api/currentUser";

const AuthGuard = () => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        checkAuthentication();
    }, []);

    const checkAuthentication = async () => {
        try {
            console.log("[AuthGuard] Starting authentication check");
            console.log("[AuthGuard] Current URL:", window.location.href);
            console.log("[AuthGuard] Document cookies:", document.cookie);
            let shouldAttemptLegacyAuth = false;

            // First, try the new custom OAuth authentication
            try {
                const customAuthResponse = await getCurrentUser();

                console.log("[AuthGuard] /auth/me response status:", customAuthResponse.status);

                if (customAuthResponse.ok) {
                    console.log("[AuthGuard] Custom auth succeeded:", customAuthResponse.user);
                    setIsAuthenticated(true);
                    return;
                }

                if (customAuthResponse.status === 401 || customAuthResponse.status === 403) {
                    console.info("[AuthGuard] Custom auth reports unauthenticated (status", customAuthResponse.status, ")");
                    setIsAuthenticated(false);
                    return;
                }

                if (customAuthResponse.status === 404) {
                    console.warn("[AuthGuard] Custom auth endpoint not found, trying legacy Azure Easy Auth");
                    shouldAttemptLegacyAuth = true;
                } else if (customAuthResponse.status >= 500) {
                    console.warn("[AuthGuard] Custom auth returned server error status", customAuthResponse.status, "- attempting legacy fallback");
                    shouldAttemptLegacyAuth = true;
                } else {
                    console.warn("[AuthGuard] Unexpected custom auth status", customAuthResponse.status, "- treating as unauthenticated");
                    setIsAuthenticated(false);
                    return;
                }
            } catch (customAuthError) {
                console.warn("[AuthGuard] Custom auth check failed, attempting legacy fallback", customAuthError);
                shouldAttemptLegacyAuth = true;
            }

            if (!shouldAttemptLegacyAuth) {
                return;
            }

            // Fallback: Check Azure Easy Auth endpoint (for existing 24 users)
            const response = await fetch("/.auth/me", {
                credentials: "same-origin"
            });
            console.log("[AuthGuard] /.auth/me response status:", response.status);

            if (response.ok) {
                const contentType = response.headers.get("content-type");
                console.log("[AuthGuard] /.auth/me content type:", contentType);

                // If response is HTML instead of JSON, auth is not properly enabled
                if (contentType && contentType.includes("text/html")) {
                    console.warn("[AuthGuard] Legacy auth endpoint returned HTML; authentication likely disabled");
                    setIsAuthenticated(false);
                    return;
                }

                const data = await response.json();
                console.log("[AuthGuard] Legacy auth payload:", JSON.stringify(data, null, 2));

                // Handle both Easy Auth response formats:
                // 1. Legacy format: Array with user info
                // 2. Azure Container Apps/Static Web Apps format: Object with clientPrincipal
                let authenticated = false;

                if (Array.isArray(data) && data.length > 0) {
                    // Legacy format: Array with user_id, userId, or user_claims
                    authenticated = !!(data[0].user_id || data[0].userId || data[0].user_claims);
                } else if (data?.clientPrincipal) {
                    // Azure Container Apps format: Object with clientPrincipal
                    const { clientPrincipal } = data;
                    authenticated = !!(
                        clientPrincipal.userId ||
                        (clientPrincipal.userRoles && clientPrincipal.userRoles.includes("authenticated")) ||
                        (clientPrincipal.claims && clientPrincipal.claims.length > 0)
                    );
                }

                console.log("[AuthGuard] Legacy auth result:", authenticated);
                setIsAuthenticated(authenticated);
            } else {
                console.warn("[AuthGuard] Legacy /.auth/me returned non-OK status:", response.status);
                setIsAuthenticated(false);
            }
        } catch (error) {
            console.error("[AuthGuard] Auth check failed with error:", error);
            setIsAuthenticated(false);
        } finally {
            console.log("[AuthGuard] Authentication check finished");
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                    fontSize: "18px",
                    color: "#666"
                }}
            >
                Loading...
            </div>
        );
    }
    // Not authenticated -> Redirect to login page
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }
    // Authenticated -> Show app content
    return <Outlet />;
};

export default AuthGuard;
